import { Divider, Flex, Text } from "@chakra-ui/react";
import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "../../../components";
import {
  ObjectHeaderBackButton,
  ObjectHeaderContainer,
} from "../../../components/ObjectHeader";
import { uppercaseFirstLetterLowercaseRest } from "../../../utils/string";
import { AiInterviewerFragment, AiInterviewerStatus } from "../../graphql";
import { AiInterviewerWizardContext } from "./AiInterviewerWizardContext";

interface WizardEditorHeaderProps {
  aiInterviewer: AiInterviewerFragment;
  saving?: boolean;
  isDirty?: boolean;
  /** Agentic flow omits the back button per Figma node 20141-137933 */
  isAgentic?: boolean;
}

/** Label + value column matching Figma node 20141-137933 field styling */
const HeaderField: React.FC<{
  label: string;
  value: string;
  valueColor?: string;
}> = ({ label, value, valueColor = "gray.700" }) => (
  <Flex direction="column" mx={5} justifyContent="center">
    <Text fontSize="xs" color="gray.500" lineHeight="16px">
      {label}
    </Text>
    <Text
      fontSize="sm"
      fontWeight="medium"
      color={valueColor}
      lineHeight="20px"
    >
      {value}
    </Text>
  </Flex>
);

/**
 * Editor header bar shown in wizard mode after guide generation.
 *
 * Classic/Flex: Figma node 20141-130529 — includes back button
 * Agentic: Figma node 20141-137933 — no back button, fields start at left edge
 *
 * 64px height, white bg, full page width, bottom divider
 */
const WizardEditorHeader: React.FC<WizardEditorHeaderProps> = ({
  aiInterviewer,
  saving,
  isDirty,
  isAgentic = false,
}) => {
  const navigate = useNavigate();
  const {
    steps,
    currentStep,
    agenticStepIndex,
    agenticSubStepIndex,
    submitVersionForm,
  } = useContext(AiInterviewerWizardContext);

  const nameValue = aiInterviewer.name?.trim() || "Untitled";
  const jobTitle = aiInterviewer.displayName || "—";
  const statusBase = uppercaseFirstLetterLowercaseRest(
    String(aiInterviewer.status)
  );
  const statusValue = isDirty ? statusBase : `${statusBase} – Saved`;

  const step = steps[currentStep];
  const onContinue = step?.onNext;

  // On the Test Interviews sub-step, show "Save" / "Launch" instead of "Exit" / "Continue"
  const isTestInterviewsStep =
    isAgentic && agenticStepIndex === 3 && agenticSubStepIndex === 2;
  const exitLabel = isTestInterviewsStep ? "Save" : "Exit";
  const continueLabel = isTestInterviewsStep ? "Launch" : "Continue";

  return (
    <>
      <ObjectHeaderContainer>
        {!isAgentic && (
          <ObjectHeaderBackButton
            backButtonLabel="Back to AI Interviewers"
            backButtonLocation="/ai-interviewers"
          />
        )}
        <HeaderField label="AI interviewer name" value={nameValue} />
        <HeaderField label="Job title" value={jobTitle} />
        <HeaderField
          label="Status"
          value={statusValue}
          valueColor={
            aiInterviewer.status === AiInterviewerStatus.Live
              ? "green.500"
              : "gray.500"
          }
        />
        <Flex ml="auto" alignItems="center" gap="2">
          {!isDirty && !saving && (
            <Flex
              w="112px"
              h="32px"
              alignItems="center"
              justifyContent="center"
            >
              <Text
                fontSize="xs"
                fontWeight={600}
                lineHeight="16px"
                color="#2DB282"
                textTransform="uppercase"
              >
                SAVED
              </Text>
            </Flex>
          )}
          {saving && (
            <Flex
              w="112px"
              h="32px"
              alignItems="center"
              justifyContent="center"
            >
              <Text
                fontSize="xs"
                fontWeight={600}
                lineHeight="16px"
                color="gray.500"
                textTransform="uppercase"
              >
                SAVING...
              </Text>
            </Flex>
          )}
          <Button
            variant="outline"
            size="sm"
            w="112px"
            fontWeight="medium"
            fontSize="sm"
            borderRadius="md"
            borderColor="gray.300"
            color="blue.600"
            _hover={{ bg: "blue.50" }}
            onClick={() => {
              if (isTestInterviewsStep && submitVersionForm) {
                submitVersionForm(() => navigate("/ai-interviewers"));
              } else {
                navigate("/ai-interviewers");
              }
            }}
          >
            {exitLabel}
          </Button>
          {onContinue && (
            <Button
              size="sm"
              w="112px"
              fontWeight="medium"
              fontSize="sm"
              borderRadius="md"
              colorScheme="blue"
              onClick={onContinue}
            >
              {continueLabel}
            </Button>
          )}
        </Flex>
      </ObjectHeaderContainer>
      <Divider />
    </>
  );
};

export { WizardEditorHeader };
